import React, {
  useContext,
  useEffect,
  useReducer,
  useState,
  useRef,
  useCallback,
} from "react";
import { Link, useLocation } from "react-router-dom";
import useHelps from "../hooks/useHelps";
import Tooltip from "@material-ui/core/Tooltip";

import {
  BarChart3,
  MessageSquare,
  Zap,
  Columns,
  User,
  Clock,
  Tag,
  HelpCircle,
  Settings,
  Users,
  ListOrdered,
  ArrowLeftRight,
  Server,
  Paperclip,
  DollarSign,
  Building2,
  ChevronDown,
  FileText,
  Table,
  List,
  UsersRound,
  Megaphone,
  MessagesSquare,
  Info,
  Code,
  Sparkles,
  Cable,
  Home,
  Cog,
  AlertTriangle,
  Wifi,
  WifiOff,
  ChevronRight,
  ShoppingCart,
  PhoneCall,
  Wrench,
  LifeBuoy,
  Scissors,
  Smile,
  Mail,
  Briefcase,
  Gavel,
  Stethoscope,
  Truck,
  FolderOpen,
  Folder,
  Share2,
  Video,
  Workflow,
  Layers,
  Bot,
  GraduationCap,
  PawPrint,
  Map,
} from "lucide-react";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";
import ColorModeContext from "../layout/themeContext";

import { Can } from "../components/Can";

import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import useSettings from "../hooks/useSettings";
import { i18n } from "../translate/i18n";
import {
  HEADER_CONFIG_SETTING_KEY,
  parseHeaderConfig,
  getDefaultSubmenuParent,
} from "../utils/headerConfig";
import rules from "../rules";
import HeaderIcon from "../components/HeaderIcon";
import {
  getModuleAccessForPath,
  hasEffectiveModulePermission,
} from "../utils/modulePermissions";
import "./mainListItems.css";

const INTERNAL_CHAT_API = "/internal-chats";

const normalizePlanBoolean = (value) => {
  if (value === true || value === false) return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return value;
};

const STRICT_FEATURE_KEYS = new Set(["useVoip", "useWallet"]);

const isPlanToggleEnabled = (plan, key) => {
  if (!plan || typeof plan !== "object") return true;
  const val = normalizePlanBoolean(plan[key]);
  if (STRICT_FEATURE_KEYS.has(key)) {
    return val === true;
  }
  return val !== false;
};

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }

  return state;
};

const CanWrapper = ({ role, perform, children, style = {} }) => {
  return (
    <Can
      role={role}
      perform={perform}
      style={style}
      yes={() => (
        <div
          className="can-wrapper"
          style={{ width: "100%", display: "block" }}
        >
          {children}
        </div>
      )}
      no={() => null}
    />
  );
};

const MainListItems = ({ isMobile = false, closeDrawer }) => {
  const colorModeContext = useContext(ColorModeContext);
  const isDarkMode = colorModeContext.mode === "dark";
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket, modulePermissions, modulePermissionsLoaded } = useContext(AuthContext);
  const { get: getSetting } = useSettings();
  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(true);
  const [showKanban, setShowKanban] = useState(true);
  const [showOpenAi, setShowOpenAi] = useState(true);
  const [showIntegrations, setShowIntegrations] = useState(true);
  const [showAsaas, setShowAsaas] = useState(true);
  const [showPdv, setShowPdv] = useState(false);
  const [showComidaNaPorta, setShowComidaNaPorta] = useState(false);
  const [showEmailModule, setShowEmailModule] = useState(true);
  const [showEmailCampaign, setShowEmailCampaign] = useState(true);
  const [showInternalPortal, setShowInternalPortal] = useState(true);
  const [showWallet, setShowWallet] = useState(true);
  const [showWalletManagement, setShowWalletManagement] = useState(true);
  const [showCallcenter, setShowCallcenter] = useState(true);
  const [showErpPhone, setShowErpPhone] = useState(true);
  const [showAnalyticsTemplateWhats, setShowAnalyticsTemplateWhats] = useState(true);
  const [showMetaAds, setShowMetaAds] = useState(true);
  const [showTasks, setShowTasks] = useState(true);
  const [showSchedules, setShowSchedules] = useState(true);
  const [showAgendacare, setShowAgendacare] = useState(true);
  const [showOdontocare, setShowOdontocare] = useState(true);
  const [showBarber, setShowBarber] = useState(true);
  const [showEducare, setShowEducare] = useState(true);
  const [showPetcare, setShowPetcare] = useState(true);
  const [showRotaVendas360, setShowRotaVendas360] = useState(true);
  const [showServico, setShowServico] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [showInternalChat, setShowInternalChat] = useState(true);
  const [showExternalApi, setShowExternalApi] = useState(true);
  const [showFinanceiro, setShowFinanceiro] = useState(true);
  const [showGroupManagement, setShowGroupManagement] = useState(true);
  const [showButtonList, setShowButtonList] = useState(true);
  const [showConsultaProcesso, setShowConsultaProcesso] = useState(true);
  const [showAdvoc360, setShowAdvoc360] = useState(true);
  const [showGatilho, setShowGatilho] = useState(true);
  const [showRedeSocial, setShowRedeSocial] = useState(true);
  const [showMeetingRooms, setShowMeetingRooms] = useState(true);
  const [showFlowBuilder, setShowFlowBuilder] = useState(true);
  const [showVoip, setShowVoip] = useState(true);
  const [showTelefonia, setShowTelefonia] = useState(true);
  const [showDashboardModule, setShowDashboardModule] = useState(true);
  const [showRelatorio, setShowRelatorio] = useState(true);
  const [showTickets, setShowTickets] = useState(true);
  // Mantido no estado para preservar sincronismo com o plano, embora o menu de tickets use a flag dedicada useTickets.
  // eslint-disable-next-line no-unused-vars
  const [showPainel, setShowPainel] = useState(true);
  const [showSuporte, setShowSuporte] = useState(true);
  const [showEquipe, setShowEquipe] = useState(true);
  const [showDocSignature, setShowDocSignature] = useState(true);
  const [showBancarioCartoes, setShowBancarioCartoes] = useState(true);
  const [showWooCommerce, setShowWooCommerce] = useState(true);
  const [showWhatsappTemplates, setShowWhatsappTemplates] = useState(true);
  const [showGroupCampaigns, setShowGroupCampaigns] = useState(true);
  const [showVoiceCampaigns, setShowVoiceCampaigns] = useState(true);
  const [planVisibilityLoaded, setPlanVisibilityLoaded] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [invisible, setInvisible] = useState(true);
  const [emailWarning, setEmailWarning] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  // eslint-disable-next-line no-unused-vars
  const [version, setVersion] = useState(false);
  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);
  const [headerConfig, setHeaderConfig] = useState(() => parseHeaderConfig());

  useEffect(() => {
    let mounted = true;
    const loadHeaderConfig = async () => {
      try {
        const setting = await getSetting(HEADER_CONFIG_SETTING_KEY);
        if (mounted) setHeaderConfig(parseHeaderConfig(setting?.value));
      } catch (_error) {
        if (mounted) setHeaderConfig(parseHeaderConfig());
      }
    };

    loadHeaderConfig();
    return () => {
      mounted = false;
    };
  }, [getSetting]);

  useEffect(() => {
    if (!socket || !user?.companyId) return undefined;

    const handleSettingsUpdate = ({ setting }) => {
      if (setting?.key === HEADER_CONFIG_SETTING_KEY) {
        setHeaderConfig(parseHeaderConfig(setting.value));
      }
    };

    socket.on(`company-${user.companyId}-settings`, handleSettingsUpdate);
    return () => socket.off(`company-${user.companyId}-settings`, handleSettingsUpdate);
  }, [socket, user?.companyId]);

  const applyPlanVisibility = useCallback((plan) => {
    const defaults = {
      showCampaigns: true,
      showKanban: true,
      showOpenAi: true,
      showIntegrations: true,
      showAsaas: true,
      showPdv: false,
      showComidaNaPorta: false,
      showEmailModule: true,
      showEmailCampaign: true,
      showInternalPortal: true,
      showWallet: true,
      showWalletManagement: true,
      showCallcenter: true,
      showSchedules: true,
      showAgendacare: true,
      showOdontocare: true,
      showBarber: true,
      showServico: true,
      showErpPhone: true,
      showAnalyticsTemplateWhats: true,
      showMetaAds: true,
      showTasks: true,
      showInternalChat: true,
      showExternalApi: true,
      showGatilho: true,
      showRedeSocial: true,
      showFlowBuilder: true,
      showVoip: true,
      showTelefonia: true,
      showDashboardModule: true,
      showRelatorio: true,
      showTickets: true,
      showPainel: true,
      showSuporte: true,
      showEquipe: true,
      showConsultaProcesso: true,
      showAdvoc360: true,
      showDocSignature: true,
      showBancarioCartoes: true,
      showWooCommerce: true,
      showMeetingRooms: true,
      showWhatsappTemplates: true,
      showGroupCampaigns: true,
      showVoiceCampaigns: true,
      showEducare: true,
      showPetcare: true,
      showRotaVendas360: true,
    };

    const visibility = plan
      ? {
          showCampaigns: isPlanToggleEnabled(plan, "useCampaigns"),
          showKanban: isPlanToggleEnabled(plan, "useKanban"),
          showOpenAi: isPlanToggleEnabled(plan, "useOpenAi"),
          showIntegrations: isPlanToggleEnabled(plan, "useIntegrations"),
          showAsaas: isPlanToggleEnabled(plan, "useAsaas"),
          showPdv: isPlanToggleEnabled(plan, "usePdv"),
          showComidaNaPorta: isPlanToggleEnabled(plan, "useComidaNaPorta"),
          showEmailModule: isPlanToggleEnabled(plan, "useEmailModule"),
          showEmailCampaign: isPlanToggleEnabled(plan, "useEmailCampaign"),
          showInternalPortal: isPlanToggleEnabled(plan, "useInternalPortal"),
          showWallet: isPlanToggleEnabled(plan, "useWallet"),
          showWalletManagement: isPlanToggleEnabled(plan, "useWalletManagement"),
          showCallcenter: isPlanToggleEnabled(plan, "useCallcenter"),
          showSchedules: isPlanToggleEnabled(plan, "useSchedules"),
          showAgendacare: isPlanToggleEnabled(plan, "useAgendacare"),
          showOdontocare: isPlanToggleEnabled(plan, "useOdontocare"),
          showBarber: isPlanToggleEnabled(plan, "useBarber"),
          showEducare: isPlanToggleEnabled(plan, "useEducare"),
          showPetcare: isPlanToggleEnabled(plan, "usePetcare"),
          showRotaVendas360: isPlanToggleEnabled(plan, "useRotaVendas360"),
          showServico: isPlanToggleEnabled(plan, "useServico"),
          showErpPhone: isPlanToggleEnabled(plan, "useErpPhone"),
          showAnalyticsTemplateWhats: isPlanToggleEnabled(plan, "useAnalyticsTemplateWhats"),
          showMetaAds: isPlanToggleEnabled(plan, "useMetaAds"),
          showTasks: isPlanToggleEnabled(plan, "useTasks"),
          showInternalChat: isPlanToggleEnabled(plan, "useInternalChat"),
          showExternalApi: isPlanToggleEnabled(plan, "useExternalApi"),
          showGatilho: isPlanToggleEnabled(plan, "useGatilho"),
          showRedeSocial: isPlanToggleEnabled(plan, "useRedeSocial"),
          showFlowBuilder: isPlanToggleEnabled(plan, "useFlowBuilder"),
          showVoip: isPlanToggleEnabled(plan, "useVoip"),
          showTelefonia: isPlanToggleEnabled(plan, "useTelefonia"),
          showDashboardModule: isPlanToggleEnabled(plan, "useDashboard"),
          showRelatorio: isPlanToggleEnabled(plan, "useRelatorio"),
          showTickets: isPlanToggleEnabled(plan, "useTickets"),
          showPainel: isPlanToggleEnabled(plan, "usePainel"),
          showSuporte: isPlanToggleEnabled(plan, "useSuporte"),
          showEquipe: isPlanToggleEnabled(plan, "useEquipe"),
          showConsultaProcesso: isPlanToggleEnabled(plan, "useConsultaProcesso"),
          showAdvoc360: isPlanToggleEnabled(plan, "useAdvoc360"),
          showDocSignature: isPlanToggleEnabled(plan, "useDocSignature"),
          showBancarioCartoes: isPlanToggleEnabled(plan, "useBancarioCartoes"),
          showWooCommerce: isPlanToggleEnabled(plan, "useWooCommerce"),
          showMeetingRooms: isPlanToggleEnabled(plan, "useMeetingRooms"),
          showWhatsappTemplates: isPlanToggleEnabled(plan, "useWhatsappTemplates"),
          showGroupCampaigns: isPlanToggleEnabled(plan, "useGroupCampaigns"),
          showVoiceCampaigns: isPlanToggleEnabled(plan, "useVoiceCampaigns"),
        }
      : defaults;

    setShowCampaigns(visibility.showCampaigns);
    setShowKanban(visibility.showKanban);
    setShowOpenAi(visibility.showOpenAi);
    setShowIntegrations(visibility.showIntegrations);
    setShowAsaas(visibility.showAsaas);
    setShowPdv(visibility.showPdv);
    setShowComidaNaPorta(visibility.showComidaNaPorta);
    setShowEmailModule(visibility.showEmailModule);
    setShowEmailCampaign(visibility.showEmailCampaign);
    setShowInternalPortal(visibility.showInternalPortal);
    setShowWallet(visibility.showWallet);
    setShowWalletManagement(visibility.showWalletManagement);
    setShowCallcenter(visibility.showCallcenter);
    setShowSchedules(visibility.showSchedules);
    setShowAgendacare(visibility.showAgendacare);
    setShowOdontocare(visibility.showOdontocare);
    setShowBarber(visibility.showBarber);
    setShowEducare(visibility.showEducare);
    setShowPetcare(visibility.showPetcare);
    setShowRotaVendas360(visibility.showRotaVendas360);
    setShowServico(visibility.showServico);
    setShowErpPhone(visibility.showErpPhone);
    setShowAnalyticsTemplateWhats(visibility.showAnalyticsTemplateWhats);
    setShowMetaAds(visibility.showMetaAds);
    setShowTasks(visibility.showTasks);
    setShowInternalChat(visibility.showInternalChat);
    setShowExternalApi(visibility.showExternalApi);
    setShowFinanceiro(plan ? isPlanToggleEnabled(plan, "useFinanceiro") : true);
    setShowGroupManagement(plan ? isPlanToggleEnabled(plan, "useGroupManagement") : true);
    setShowButtonList(plan ? isPlanToggleEnabled(plan, "useButtonList") : true);
    setShowConsultaProcesso(visibility.showConsultaProcesso);
    setShowAdvoc360(visibility.showAdvoc360);
    setShowDocSignature(visibility.showDocSignature);
    setShowBancarioCartoes(visibility.showBancarioCartoes);
    setShowWooCommerce(visibility.showWooCommerce);
    setShowMeetingRooms(visibility.showMeetingRooms);
    setShowWhatsappTemplates(visibility.showWhatsappTemplates);
    setShowGroupCampaigns(visibility.showGroupCampaigns);
    setShowVoiceCampaigns(visibility.showVoiceCampaigns);
    setShowGatilho(visibility.showGatilho);
    setShowRedeSocial(visibility.showRedeSocial);
    setShowFlowBuilder(visibility.showFlowBuilder);
    setShowVoip(visibility.showVoip);
    setShowTelefonia(visibility.showTelefonia);
    setShowDashboardModule(visibility.showDashboardModule);
    setShowRelatorio(visibility.showRelatorio);
    setShowTickets(visibility.showTickets);
    setShowPainel(visibility.showPainel);
    setShowSuporte(visibility.showSuporte);
    setShowEquipe(visibility.showEquipe);
  }, []);

  const [managementMenuOpen, setManagementMenuOpen] = useState(false);
  const [ticketsMenuOpen, setTicketsMenuOpen] = useState(false);
  const [modulesMenuOpen, setModulesMenuOpen] = useState(false);
  const [campaignsMenuOpen, setCampaignsMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [activeDesktopSection, setActiveDesktopSection] = useState("");

  const managementMenuRef = useRef(null);
  const managementButtonRef = useRef(null);
  const ticketsMenuRef = useRef(null);
  const ticketsButtonRef = useRef(null);
  const modulesMenuRef = useRef(null);
  const modulesButtonRef = useRef(null);
  const campaignsMenuRef = useRef(null);
  const campaignsButtonRef = useRef(null);
  const adminMenuRef = useRef(null);
  const adminButtonRef = useRef(null);

  const managementContainerRef = useRef(null);
  const ticketsContainerRef = useRef(null);
  const modulesContainerRef = useRef(null);
  const campaignsContainerRef = useRef(null);
  const adminContainerRef = useRef(null);
  const desktopCloseTimeoutRef = useRef(null);
  const emailUnreadRef = useRef(-1);

  const clearDesktopCloseTimeout = useCallback(() => {
    if (desktopCloseTimeoutRef.current) {
      clearTimeout(desktopCloseTimeoutRef.current);
      desktopCloseTimeoutRef.current = null;
    }
  }, []);

  const openDesktopMenu = useCallback(
    (menu) => {
      if (isMobile) return;

      clearDesktopCloseTimeout();
      setManagementMenuOpen(menu === "management");
      setTicketsMenuOpen(menu === "tickets");
      setModulesMenuOpen(menu === "modules");
      setCampaignsMenuOpen(menu === "campaigns");
      setAdminMenuOpen(menu === "admin");
    },
    [clearDesktopCloseTimeout, isMobile]
  );

  const scheduleDesktopClose = useCallback(() => {
    if (isMobile) return;

    clearDesktopCloseTimeout();
    desktopCloseTimeoutRef.current = setTimeout(() => {
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setModulesMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
      setActiveDesktopSection("");
    }, 180);
  }, [clearDesktopCloseTimeout, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      function handleDesktopClickOutside(event) {
        const containers = [
          managementContainerRef.current,
          ticketsContainerRef.current,
          modulesContainerRef.current,
          campaignsContainerRef.current,
          adminContainerRef.current,
        ].filter(Boolean);

        const clickedInsideAnyContainer = containers.some((container) =>
          container.contains(event.target)
        );

        if (!clickedInsideAnyContainer) {
          setActiveDesktopSection("");
          setManagementMenuOpen(false);
          setTicketsMenuOpen(false);
          setModulesMenuOpen(false);
          setCampaignsMenuOpen(false);
          setAdminMenuOpen(false);
        }
      }

      document.addEventListener("mousedown", handleDesktopClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleDesktopClickOutside);
      };
    }
  }, [isMobile]);

  useEffect(() => {
    return () => {
      clearDesktopCloseTimeout();
    };
  }, [clearDesktopCloseTimeout]);

  useEffect(() => {
    if (isMobile) {
      function handleClickOutside(event) {
        if (
          managementMenuOpen &&
          managementButtonRef.current &&
          !managementButtonRef.current.contains(event.target) &&
          managementMenuRef.current &&
          !managementMenuRef.current.contains(event.target)
        ) {
          setManagementMenuOpen(false);
        }

        if (
          ticketsMenuOpen &&
          ticketsButtonRef.current &&
          !ticketsButtonRef.current.contains(event.target) &&
          ticketsMenuRef.current &&
          !ticketsMenuRef.current.contains(event.target)
        ) {
          setTicketsMenuOpen(false);
        }

        if (
          modulesMenuOpen &&
          modulesButtonRef.current &&
          !modulesButtonRef.current.contains(event.target) &&
          modulesMenuRef.current &&
          !modulesMenuRef.current.contains(event.target)
        ) {
          setModulesMenuOpen(false);
        }

        if (
          campaignsMenuOpen &&
          campaignsButtonRef.current &&
          !campaignsButtonRef.current.contains(event.target) &&
          campaignsMenuRef.current &&
          !campaignsMenuRef.current.contains(event.target)
        ) {
          setCampaignsMenuOpen(false);
        }

        if (
          adminMenuOpen &&
          adminButtonRef.current &&
          !adminButtonRef.current.contains(event.target) &&
          adminMenuRef.current &&
          !adminMenuRef.current.contains(event.target)
        ) {
          setAdminMenuOpen(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [
    managementMenuOpen,
    ticketsMenuOpen,
    modulesMenuOpen,
    campaignsMenuOpen,
    adminMenuOpen,
    isMobile,
  ]);

  const fetchUnreadEmails = useCallback(async () => {
    try {
      const { data } = await api.get("/api/email/accounts");
      const accounts = Array.isArray(data) ? data : [];

      if (!accounts.length) {
        emailUnreadRef.current = 0;
        setEmailWarning(false);
        return;
      }

      const counts = await Promise.all(
        accounts.map(async (account) => {
          try {
            const response = await api.get("/api/email/messages", {
              params: {
                accountId: account.id,
                isRead: "false",
                pageNumber: 1
              }
            });
            return Number(response?.data?.count || 0);
          } catch (error) {
            return 0;
          }
        })
      );

      const unreadTotal = counts.reduce((sum, value) => sum + Number(value || 0), 0);

      if (location.pathname.startsWith("/emails")) {
        emailUnreadRef.current = unreadTotal;
        setEmailWarning(false);
        return;
      }

      if (emailUnreadRef.current < 0) {
        setEmailWarning(unreadTotal > 0);
      } else if (unreadTotal > emailUnreadRef.current) {
        setEmailWarning(true);
      } else if (unreadTotal === 0) {
        setEmailWarning(false);
      }

      emailUnreadRef.current = unreadTotal;
    } catch (error) {
      // Silent fail
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchUnreadEmails();
    const timer = setInterval(fetchUnreadEmails, 45000);
    return () => clearInterval(timer);
  }, [fetchUnreadEmails]);

  useEffect(() => {
    if (location.pathname.startsWith("/emails")) {
      setEmailWarning(false);
    }
  }, [location.pathname]);

  const closeAllMenus = () => {
    setManagementMenuOpen(false);
    setTicketsMenuOpen(false);
    setModulesMenuOpen(false);
    setCampaignsMenuOpen(false);
    setAdminMenuOpen(false);
    setActiveDesktopSection("");

    if (isMobile && closeDrawer) {
      closeDrawer();
    }
  };

  const handleMenuItemClick = (to) => {
    if (isMobile && closeDrawer) {
      closeDrawer();
    }
    closeAllMenus();
  };

  const toggleManagementMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();

      if (managementMenuOpen && isManagementActive && closeDrawer) {
        closeDrawer();
        return;
      }

      setManagementMenuOpen(!managementMenuOpen);
      setTicketsMenuOpen(false);
      setModulesMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
    } else {
      e.preventDefault();
      e.stopPropagation();
      setManagementMenuOpen((prev) => !prev);
      setTicketsMenuOpen(false);
      setModulesMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
      setActiveDesktopSection("");
    }
  };

  const toggleTicketsMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();

      if (ticketsMenuOpen && isTicketsActive && closeDrawer) {
        closeDrawer();
        return;
      }

      setTicketsMenuOpen(!ticketsMenuOpen);
      setManagementMenuOpen(false);
      setModulesMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
    } else {
      e.preventDefault();
      e.stopPropagation();
      const nextOpen = !ticketsMenuOpen;
      setTicketsMenuOpen(nextOpen);
      setManagementMenuOpen(false);
      setModulesMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
      setActiveDesktopSection(
        nextOpen ? getSectionKey(i18n.t("mainDrawer.listItems.tickets")) : ""
      );
    }
  };

  const toggleModulesMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();

      if (modulesMenuOpen && isModulesActive && closeDrawer) {
        closeDrawer();
        return;
      }

      setModulesMenuOpen(!modulesMenuOpen);
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
    } else {
      e.preventDefault();
      e.stopPropagation();
      const nextOpen = !modulesMenuOpen;
      setModulesMenuOpen(nextOpen);
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setCampaignsMenuOpen(false);
      setAdminMenuOpen(false);
      setActiveDesktopSection(
        nextOpen ? getSectionKey("Módulos") : ""
      );
    }
  };

  const toggleCampaignsMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();

      if (campaignsMenuOpen && isCampaignRouteActive && closeDrawer) {
        closeDrawer();
        return;
      }

      setCampaignsMenuOpen(!campaignsMenuOpen);
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setModulesMenuOpen(false);
      setAdminMenuOpen(false);
    } else {
      e.preventDefault();
      e.stopPropagation();
      setCampaignsMenuOpen((prev) => !prev);
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setModulesMenuOpen(false);
      setAdminMenuOpen(false);
      setActiveDesktopSection("");
    }
  };

  const toggleAdminMenu = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();

      if (adminMenuOpen && isAdminActive && closeDrawer) {
        closeDrawer();
        return;
      }

      setAdminMenuOpen(!adminMenuOpen);
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setModulesMenuOpen(false);
      setCampaignsMenuOpen(false);
    } else {
      e.preventDefault();
      e.stopPropagation();
      const nextOpen = !adminMenuOpen;
      setAdminMenuOpen(nextOpen);
      setManagementMenuOpen(false);
      setTicketsMenuOpen(false);
      setModulesMenuOpen(false);
      setCampaignsMenuOpen(false);
      setActiveDesktopSection(nextOpen ? getSectionKey("Administração") : "");
    }
  };

  const isManagementActive =
    location.pathname === "/" ||
    location.pathname.startsWith("/analytics-template-whats") ||
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith("/moments") ||
    location.pathname.startsWith("/meta-ads");

  const isTicketsActive =
    location.pathname.startsWith("/tickets") ||
    location.pathname.startsWith("/emails") ||
    location.pathname.startsWith("/quick-messages") ||
    location.pathname.startsWith("/support-tickets") ||
    location.pathname.startsWith("/kanban") ||
    location.pathname.startsWith("/chats") ||
    location.pathname.startsWith("/contacts") ||
    location.pathname.startsWith("/tags") ||
    location.pathname.startsWith("/helps") ||
    location.pathname.startsWith("/todolist") ||
    location.pathname.startsWith("/schedules") ||
    location.pathname.startsWith("/my-wallet") ||
    location.pathname.startsWith("/wallet-sector");

  const isModulesActive =
    location.pathname.startsWith("/consulta-processo") ||
    location.pathname.startsWith("/advoc360") ||
    location.pathname.startsWith("/agendacare") ||
    location.pathname.startsWith("/odontocare") ||
    location.pathname.startsWith("/barber") ||
    location.pathname.startsWith("/educare") ||
    location.pathname.startsWith("/petcare") ||
    location.pathname.startsWith("/services") ||
    location.pathname.startsWith("/pdv") ||
    location.pathname.startsWith("/loja-online") ||
    location.pathname.startsWith("/comida-na-porta") ||
    location.pathname.startsWith("/motoqueiro") ||
    location.pathname.startsWith("/callcenter") ||
    location.pathname.startsWith("/erpfone") ||
    location.pathname.startsWith("/voip") ||
    location.pathname.startsWith("/meeting-rooms") ||
    location.pathname.startsWith("/woocommerce");

  const isCampaignRouteActive =
    location.pathname === "/campaigns" ||
    location.pathname === "/voice-campaigns" ||
    location.pathname === "/group-campaigns" ||
    location.pathname === "/email-campaigns" ||
    location.pathname.startsWith("/contact-lists") ||
    location.pathname.startsWith("/campaigns-config");

  const isAdminActive =
    location.pathname.startsWith("/users") ||
    location.pathname.startsWith("/queues") ||
    location.pathname.startsWith("/equipes") ||
    location.pathname.startsWith("/group-management") ||
    location.pathname.startsWith("/prompts") ||
    location.pathname.startsWith("/ai-agents") ||
    location.pathname.startsWith("/ai-help-requests") ||
    location.pathname.startsWith("/ai-secretaria") ||
    location.pathname.startsWith("/mcp-agents") ||
    location.pathname.startsWith("/queue-integration") ||
    location.pathname.startsWith("/messages-api") ||
    location.pathname.startsWith("/knowledge-base") ||
    location.pathname.startsWith("/evolution-interactives") ||
    location.pathname.startsWith("/asaas-financial") ||
    location.pathname.startsWith("/financeiro") ||
    location.pathname.startsWith("/wallet-management") ||
    location.pathname.startsWith("/callcenter-central") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/portal-interno") ||
    location.pathname.startsWith("/announcements") ||
    location.pathname.startsWith("/companies") ||
    location.pathname.startsWith("/connections") ||
    location.pathname.startsWith("/allConnections");

  const { getPlanCompany } = usePlans();
  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = async () => {
      try {
        const helps = await list();
        setHasHelps(helps.length > 0);
      } catch (_) {}
    };
    document.addEventListener("helps:changed", handler);
    return () => document.removeEventListener("helps:changed", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      if (!companyId) return;

      try {
        const planConfigs = await getPlanCompany(undefined, companyId);
        applyPlanVisibility(planConfigs?.plan);
        setPlanVisibilityLoaded(true);
      } catch (error) {
        applyPlanVisibility();
        setPlanVisibilityLoaded(true);

        setTimeout(() => {
          fetchData();
        }, 5000);
      }
    }

    fetchData();

    const handlePlansChanged = () => {
      fetchData();
    };

    window.addEventListener("plans:changed", handlePlansChanged);

    return () => {
      window.removeEventListener("plans:changed", handlePlansChanged);
    };
  }, [applyPlanVisibility, getPlanCompany, user?.companyId]);

  useEffect(() => {
    let active = true;

    const fetchMeetingSettings = async () => {
      if (!user?.companyId) return;
      try {
        const { data } = await api.get("/meeting-settings/current");
        if (active) setShowMeetingRooms(data?.enabled === true);
      } catch (_) {
        if (active) setShowMeetingRooms(false);
      }
    };

    const handleSettingsChanged = (event) => {
      if (Number(event?.detail?.companyId) === Number(user?.companyId)) {
        setShowMeetingRooms(event?.detail?.enabled === true);
      }
    };

    fetchMeetingSettings();
    window.addEventListener("meeting-settings:changed", handleSettingsChanged);
    return () => {
      active = false;
      window.removeEventListener(
        "meeting-settings:changed",
        handleSettingsChanged
      );
    };
  }, [user?.companyId]);

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id) {
      const companyId = user.companyId;
      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatMainListItems);
      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatMainListItems);
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get(`${INTERNAL_CHAT_API}/`, {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const getConnectionStatusClass = () => {
    if (!connectionWarning) return "success";

    const offlineWhats = whatsApps.filter((whats) => {
      return whats.status === "DISCONNECTED" || whats.status === "TIMEOUT";
    });

    if (offlineWhats.length > 0) {
      return "error";
    }

    return "warning";
  };

  const getConnectionStatusText = () => {
    const disconnectedCount = whatsApps.filter((whats) => {
      return (
        whats.status === "qrcode" ||
        whats.status === "PAIRING" ||
        whats.status === "DISCONNECTED" ||
        whats.status === "TIMEOUT" ||
        whats.status === "OPENING"
      );
    }).length;

    const connectedCount = whatsApps.length - disconnectedCount;

    if (disconnectedCount > 0) {
      return `${connectedCount}/${whatsApps.length}`;
    }

    return i18n.t("connections.check");
  };

  const getConnectionStatusIcon = () => {
    if (!connectionWarning) return <Wifi className="connection-icon" />;

    const offlineWhats = whatsApps.filter((whats) => {
      return whats.status === "DISCONNECTED" || whats.status === "TIMEOUT";
    });

    if (offlineWhats.length > 0) {
      return <WifiOff className="connection-icon" />;
    }

    return <AlertTriangle className="connection-icon" />;
  };

  const hasPermission = (role, action) => {
    const permissions = rules[role];

    if (!permissions) {
      return false;
    }

    return permissions.static?.includes(action) || false;
  };

  const hasModulePermission = (key) => {
    const isExplicitlyDisabled =
      String(user?.[key] || "").toLowerCase() === "disabled" ||
      user?.[key] === false;
    if (isExplicitlyDisabled) return false;

    const teamPermissions = user?.team?.permissions || {};
    const isTeamDisabled =
      teamPermissions?.[key] === false ||
      String(teamPermissions?.[key] || "").toLowerCase() === "disabled";
    const isUserExplicitlyEnabled =
      user?.[key] === true ||
      String(user?.[key] || "").toLowerCase() === "enabled";

    if (isTeamDisabled && !isUserExplicitlyEnabled) return false;
    if (isUserExplicitlyEnabled) return true;

    const userPermissions = user?.permissions || {};

    const map = {
      allowAttendance: ["visualizar_tickets", "responder_tickets"],
      showDashboard: ["acessar_dashboard"],
      allowUsers: ["gerenciar_usuarios"],
      allowUserProfile: ["gerenciar_usuarios"],
      allowQueues: ["gerenciar_usuarios"],
      allowTeams: ["gerenciar_usuarios"],
      allowCampaigns: ["enviar_campanhas"],
      allowCampaignsList: ["enviar_campanhas"],
      allowFinanceiro: ["acessar_relatorios"],
    };

    const hybridKeys = map[key] || [];
    if (hybridKeys.length === 0) return true;
    return hybridKeys.some(
      (permKey) => Boolean(teamPermissions?.[permKey]) || Boolean(userPermissions?.[permKey])
    );
  };

  const hasModuleAccess = (key) =>
    modulePermissionsLoaded &&
    hasEffectiveModulePermission(modulePermissions, key, user);

  const getSectionKey = (title, className = "") =>
    `${className}-${title}`
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-_]/g, "");

  const getSubmenuEffectiveParent = (to) => {
    const custom = headerConfig?.submenus?.find((item) => item.path === to);
    return custom?.parent || getDefaultSubmenuParent(to);
  };

  const renderMenuItem = (to, text, icon, showBadge = false, ownerParent = null, currentRenderingParent = null) => (
    (() => {
      const submenu = headerConfig?.submenus?.find((item) => item.path === to);
      if (submenu?.visible === false) return null;

      const requiredModulePermission = getModuleAccessForPath(to);
      if (requiredModulePermission && !hasModuleAccess(requiredModulePermission)) {
        return null;
      }

      const targetParent = currentRenderingParent || ownerParent;
      if (targetParent) {
        const effectiveParent = getSubmenuEffectiveParent(to);
        if (effectiveParent !== targetParent) {
          return null;
        }
      }

      const configuredIcon = submenu?.icon ? (
        <HeaderIcon icon={submenu.icon} className="hero-icon" fallback={icon} />
      ) : (
        icon
      );
      return <Link
      key={to}
      to={to}
      className="dropdown-menu-item"
      onClick={() => handleMenuItemClick(to)}
    >
      <div className="menu-item-icon">
        {showBadge ? (
          <div className="badge-container">
            {icon}
            <span className="badge">!</span>
          </div>
        ) : (
          configuredIcon
        )}
      </div>
      <span className="menu-item-text">{submenu?.label || text}</span>
      {to === "/connections" && connectionWarning && (
        <Tooltip
          title={i18n.t("connections.title") + ": " + getConnectionStatusText()}
          placement="right"
          arrow
        >
          <div className={`connection-status ${getConnectionStatusClass()}`}>
            {getConnectionStatusIcon()}
            <span>{getConnectionStatusText()}</span>
          </div>
        </Tooltip>
      )}
      </Link>;
    })()
  );

  const renderMenuSection = (title, items, className = "") => {
    const visibleItems = React.Children.toArray(items.filter(Boolean));
    const sectionKey = getSectionKey(title, className);
    const isSectionOpen = isMobile ? openSections[sectionKey] === true : true;
    const isDesktopSectionActive =
      !isMobile && activeDesktopSection === sectionKey;

    if (!visibleItems.length) {
      return null;
    }

    return (
      <div
        className={`dropdown-menu-section ${
          isMobile ? "" : "desktop-lateral"
        } ${
          isDesktopSectionActive ? "active" : ""
        } ${className}`.trim()}
      >
        {isMobile ? (
          <button
            type="button"
            className={`dropdown-menu-section-toggle ${
              isSectionOpen ? "open" : ""
            }`}
            onClick={() =>
              setOpenSections((prev) => ({
                ...prev,
                [sectionKey]: prev[sectionKey] !== true,
              }))
            }
          >
            <span className="dropdown-menu-section-title">{title}</span>
            <ChevronDown
              className={`section-toggle-icon ${isSectionOpen ? "open" : ""}`}
            />
          </button>
        ) : (
          <button
            type="button"
            className="dropdown-menu-section-trigger"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveDesktopSection((prev) =>
                prev === sectionKey ? "" : sectionKey
              );
            }}
          >
            <span className="dropdown-menu-section-title">{title}</span>
            <ChevronRight
              className={`section-trigger-icon ${
                isDesktopSectionActive ? "open" : ""
              }`}
            />
          </button>
        )}
        <div
          className={`dropdown-menu-section-items ${
            isMobile ? "mobile-collapsible" : ""
          } ${isSectionOpen ? "open" : ""}`.trim()}
        >
          {visibleItems}
        </div>
      </div>
    );
  };

  const renderMenuSubmenu = (
    title,
    icon,
    items,
    className = "",
    isActive = false
  ) => {
    const visibleItems = React.Children.toArray(items.filter(Boolean));
    const sectionKey = getSectionKey(title, className);
    const isSectionOpen =
      openSections[sectionKey] === true || (isActive && openSections[sectionKey] !== false);

    if (!visibleItems.length) {
      return null;
    }

    return (
      <div
        className={`dropdown-menu-submenu ${
          isSectionOpen ? "active" : ""
        } ${isActive ? "route-active" : ""} ${className}`.trim()}
      >
        <button
          type="button"
          className={`dropdown-menu-submenu-toggle ${
            isSectionOpen ? "open" : ""
          } ${isActive ? "active" : ""}`.trim()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpenSections((prev) => ({
              ...prev,
              [sectionKey]: prev[sectionKey] !== true,
            }));
          }}
        >
          <div className="menu-item-icon">{icon}</div>
          <span className="menu-item-text">{title}</span>
          <ChevronDown
            className={`submenu-toggle-icon ${
              isSectionOpen ? "open" : ""
            }`}
          />
        </button>

        <div
          className={`dropdown-menu-submenu-items mobile-collapsible ${
            isSectionOpen ? "open" : ""
          }`.trim()}
        >
          {visibleItems}
        </div>
      </div>
    );
  };

  const hasSubmenusForMenu = (menuKey) => {
    if (!headerConfig?.submenus) return true;
    return headerConfig.submenus.some((s) => s.visible !== false && s.parent === menuKey);
  };

  const getHeaderMenu = (key) =>
    headerConfig.menus.find((menu) => menu.key === key) ||
    parseHeaderConfig().menus.find((menu) => menu.key === key);

  const getHeaderMenuStyle = (key) => {
    const menu = getHeaderMenu(key);
    return menu?.visible === false || !hasSubmenusForMenu(key) ? { display: "none" } : { order: menu?.order };
  };

  const renderHeaderMenuIcon = (key, fallback) => {
    const iconName = getHeaderMenu(key)?.icon;
    if (iconName) {
      return (
        <HeaderIcon
          icon={iconName}
          className="nav-button-icon"
          fallback={React.createElement(fallback, { className: "nav-button-icon" })}
        />
      );
    }
    const FallbackComp = fallback;
    return <FallbackComp className="nav-button-icon" />;
  };

  const canShowJuridicoSection =
    (showConsultaProcesso && hasModuleAccess("process_consultation.access")) ||
    (showAdvoc360 && hasModuleAccess("advoc360.access"));

  const canAccessMeetingRooms =
    showMeetingRooms && hasModuleAccess("meeting_rooms.access");

  const canShowRedeSocialSection =
    (showGatilho && hasModuleAccess("social_triggers.access")) ||
    (showFlowBuilder && hasModuleAccess("flow_app.access")) ||
    (showRedeSocial && hasModuleAccess("social_network.access")) ||
    (showWhatsappTemplates && hasModuleAccess("whatsapp_templates.access")) ||
    canAccessMeetingRooms;

  const canShowSaudeBelezaSection =
    (showAgendacare && hasModuleAccess("agenda_care.access")) ||
    (showOdontocare && hasModuleAccess("odonto_care.access")) ||
    (showBarber && hasModuleAccess("barber.access")) ||
    showEducare ||
    showRotaVendas360 ||
    (showPetcare && hasModuleAccess("petcare.access")) ||
    (showServico && hasModuleAccess("services.access"));

  const canShowVendasDeliverySection =
    (showPdv && hasModuleAccess("pdv.access")) ||
    (showComidaNaPorta &&
      (hasModuleAccess("comida_na_porta.access") ||
        hasModuleAccess("motoqueiro.access"))) ||
    (showWooCommerce && hasModuleAccess("woocommerce.access"));

  const canShowTelefoniaSection =
    planVisibilityLoaded &&
    showTelefonia &&
    ((showCallcenter &&
      (hasModuleAccess("callcenter.access") ||
        hasModuleAccess("callcenter_central.access"))) ||
      (showErpPhone && hasModuleAccess("erp_phone.access")) ||
      (showVoip && hasModuleAccess("voip.access")));

  const canShowFinanceiroModuloSection =
    (showAsaas && hasModuleAccess("asaas.access")) ||
    (showBancarioCartoes && hasModuleAccess("banking_cards.access"));

  const canShowManagementMenu =
    planVisibilityLoaded && (showDashboardModule || showRelatorio);

  // Verifica se há algum módulo específico visível para mostrar o menu "Módulos"
  const hasAnySpecificModule =
    (showDocSignature && hasModuleAccess("document_signature.access")) ||
    canShowJuridicoSection ||
    canShowRedeSocialSection ||
    canShowSaudeBelezaSection ||
    canShowVendasDeliverySection ||
    canShowTelefoniaSection ||
    canShowFinanceiroModuloSection;

  const itemRenderers = {
    "/": (currentParent) => showDashboardModule && renderMenuItem("/", "Dashboard", <BarChart3 className="hero-icon" />, false, "management", currentParent),
    "/reports": (currentParent) => showRelatorio && renderMenuItem("/reports", i18n.t("mainDrawer.listItems.reports"), <FileText className="hero-icon" />, false, "management", currentParent),
    "/analytics-template-whats": (currentParent) => showDashboardModule && showAnalyticsTemplateWhats && renderMenuItem("/analytics-template-whats", "Analytics Template Whats", <BarChart3 className="hero-icon" />, false, "management", currentParent),
    "/meta-ads/campaigns": (currentParent) => showDashboardModule && showMetaAds && renderMenuItem("/meta-ads/campaigns", "Campanhas", <Megaphone className="hero-icon" />, false, "management", currentParent),
    "/meta-ads/analytics": (currentParent) => showDashboardModule && showMetaAds && renderMenuItem("/meta-ads/analytics", "Analytics", <BarChart3 className="hero-icon" />, false, "management", currentParent),
    "/meta-ads/reports": (currentParent) => showDashboardModule && showMetaAds && renderMenuItem("/meta-ads/reports", "Relatórios", <FileText className="hero-icon" />, false, "management", currentParent),
    "/meta-ads/roi": (currentParent) => showDashboardModule && showMetaAds && renderMenuItem("/meta-ads/roi", "ROI", <DollarSign className="hero-icon" />, false, "management", currentParent),
    "/moments": (currentParent) => showDashboardModule && renderMenuItem("/moments", i18n.t("mainDrawer.listItems.chatsTempoReal"), <Table className="hero-icon" />, false, "management", currentParent),

    "/tickets": (currentParent) => showTickets && renderMenuItem("/tickets", i18n.t("mainDrawer.listItems.tickets"), <MessagesSquare className="hero-icon" />, false, "attendance", currentParent),
    "/emails": (currentParent) => showEmailModule && hasModulePermission("allowEmail") && renderMenuItem("/emails", "E-mail", <Paperclip className="hero-icon" />, emailWarning, "attendance", currentParent),
    "/quick-messages": (currentParent) => renderMenuItem("/quick-messages", i18n.t("mainDrawer.listItems.quickMessages"), <Zap className="hero-icon" />, false, "attendance", currentParent),
    "/support-tickets": (currentParent) => showSuporte && renderMenuItem("/support-tickets", "Suporte", <LifeBuoy className="hero-icon" />, false, "attendance", currentParent),
    "/portal-interno": (currentParent) => showInternalPortal && hasModulePermission("allowInternalPortal") && renderMenuItem("/portal-interno", "Portal Interno", <Info className="hero-icon" />, false, "attendance", currentParent),
    "/kanban": (currentParent) => showKanban && user?.profile !== "motoboy" && hasModulePermission("allowKanban") && renderMenuItem("/kanban", i18n.t("mainDrawer.listItems.kanban"), <Columns className="hero-icon" />, false, "attendance", currentParent),
    "/chats": (currentParent) => showInternalChat && hasModulePermission("allowInternalChat") && renderMenuItem("/chats", i18n.t("mainDrawer.listItems.chats"), <div className={`notification-icon ${invisible ? "" : "has-notification"}`}><MessageSquare className="hero-icon" /></div>, false, "attendance", currentParent),
    "/contacts": (currentParent) => hasModulePermission("allowContacts") && renderMenuItem("/contacts", i18n.t("mainDrawer.listItems.contacts"), <User className="hero-icon" />, false, "attendance", currentParent),
    "/my-wallet": (currentParent) => showWallet && hasModulePermission("allowMyWallet") && renderMenuItem("/my-wallet", "Minha Carteira", <DollarSign className="hero-icon" />, false, "attendance", currentParent),
    "/tags": (currentParent) => hasModulePermission("allowTags") && renderMenuItem("/tags", i18n.t("mainDrawer.listItems.tags"), <Tag className="hero-icon" />, false, "attendance", currentParent),
    "/todolist": (currentParent) => showTasks && hasModulePermission("allowTasks") && renderMenuItem("/todolist", i18n.t("todo.task"), <List className="hero-icon" />, false, "attendance", currentParent),
    "/schedules": (currentParent) => showSchedules && hasModulePermission("allowSchedules") && renderMenuItem("/schedules", i18n.t("mainDrawer.listItems.schedules"), <Clock className="hero-icon" />, false, "attendance", currentParent),
    "/helps": (currentParent) => hasHelps && renderMenuItem("/helps", i18n.t("mainDrawer.listItems.helps"), <HelpCircle className="hero-icon" />, false, "attendance", currentParent),

    "/document-signature": (currentParent) => showDocSignature && hasModuleAccess("document_signature.access") && renderMenuItem("/document-signature", "Assinar com Certificado", <FileText className="hero-icon" />, false, "modules", currentParent),
    "/consulta-processo": (currentParent) => showConsultaProcesso && hasModuleAccess("process_consultation.access") && renderMenuItem("/consulta-processo", "Consulta Processo", <Briefcase className="hero-icon" />, false, "modules", currentParent),
    "/advoc360": (currentParent) => showAdvoc360 && hasModuleAccess("advoc360.access") && renderMenuItem("/advoc360", "Advoc-360", <Gavel className="hero-icon" />, false, "modules", currentParent),
    "/social-triggers": (currentParent) => showGatilho && hasModuleAccess("social_triggers.access") && renderMenuItem("/social-triggers", "Gatilhos", <Share2 className="hero-icon" />, false, "modules", currentParent),
    "/flow-process": (currentParent) => showFlowBuilder && hasModuleAccess("flow_app.access") && renderMenuItem("/flow-process", "Processos", <Workflow className="hero-icon" />, false, "modules", currentParent),
    "/flow-process/templates": (currentParent) => showFlowBuilder && hasModuleAccess("flow_app.access") && renderMenuItem("/flow-process/templates", "Templates", <Layers className="hero-icon" />, false, "modules", currentParent),
    "/flow-process/categories": (currentParent) => showFlowBuilder && hasModuleAccess("flow_app.access") && renderMenuItem("/flow-process/categories", "Categorias", <Folder className="hero-icon" />, false, "modules", currentParent),
    "/flow-process/reports": (currentParent) => showFlowBuilder && hasModuleAccess("flow_app.access") && renderMenuItem("/flow-process/reports", "Relatórios", <BarChart3 className="hero-icon" />, false, "modules", currentParent),
    "/social-posts": (currentParent) => showRedeSocial && hasModuleAccess("social_network.access") && renderMenuItem("/social-posts", "Rede Social", <Share2 className="hero-icon" />, false, "modules", currentParent),
    "/whatsapp-templates": (currentParent) => showWhatsappTemplates && hasModuleAccess("whatsapp_templates.access") && renderMenuItem("/whatsapp-templates", "Whatsapp Templates", <FileText className="hero-icon" />, false, "modules", currentParent),
    "/meeting-rooms": (currentParent) => canAccessMeetingRooms && renderMenuItem("/meeting-rooms", "Sala de reunião", <Video className="hero-icon" />, false, "modules", currentParent),
    "/agendacare": (currentParent) => showAgendacare && hasModuleAccess("agenda_care.access") && renderMenuItem("/agendacare", "AgendaCare", <Stethoscope className="hero-icon" />, false, "modules", currentParent),
    "/odontocare": (currentParent) => showOdontocare && hasModuleAccess("odonto_care.access") && renderMenuItem("/odontocare", "OdontoCare", <Smile className="hero-icon" />, false, "modules", currentParent),
    "/barber": (currentParent) => showBarber && hasModuleAccess("barber.access") && renderMenuItem("/barber", "Barbearia", <Scissors className="hero-icon" />, false, "modules", currentParent),
    "/educare": (currentParent) => showEducare && renderMenuItem("/educare", "EduCare", <GraduationCap className="hero-icon" />, false, "modules", currentParent),
    "/petcare": (currentParent) => showPetcare && hasModuleAccess("petcare.access") && renderMenuItem("/petcare", "PetCare 360", <PawPrint className="hero-icon" />, false, "modules", currentParent),
    "/rotsa_360": (currentParent) => showRotaVendas360 && renderMenuItem("/rotsa_360", "RotaVendas 360", <Map className="hero-icon" />, false, "modules", currentParent),
    "/services": (currentParent) => showServico && hasModuleAccess("services.access") && renderMenuItem("/services", "Serviços", <Scissors className="hero-icon" />, false, "modules", currentParent),
    "/pdv": (currentParent) => showPdv && hasModuleAccess("pdv.access") && renderMenuItem("/pdv", "Caixa PDV", <ShoppingCart className="hero-icon" />, false, "modules", currentParent),
    "/loja-online": (currentParent) => showPdv && hasModuleAccess("pdv.access") && renderMenuItem("/loja-online", "Loja Online", <ShoppingCart className="hero-icon" />, false, "modules", currentParent),
    "/comida-na-porta": (currentParent) => showComidaNaPorta && hasModuleAccess("comida_na_porta.access") && renderMenuItem("/comida-na-porta", "Comida na Porta", <ShoppingCart className="hero-icon" />, false, "modules", currentParent),
    "/motoqueiro": (currentParent) => showComidaNaPorta && hasModuleAccess("motoqueiro.access") && renderMenuItem("/motoqueiro", "Motoqueiro", <Truck className="hero-icon" />, false, "modules", currentParent),
    "/woocommerce": (currentParent) => showWooCommerce && hasModuleAccess("woocommerce.access") && renderMenuItem("/woocommerce", "WooCommerce", <ShoppingCart className="hero-icon" />, false, "modules", currentParent),
    "/callcenter": (currentParent) => showCallcenter && showTelefonia && hasModuleAccess("callcenter.access") && renderMenuItem("/callcenter", "Callcenter", <PhoneCall className="hero-icon" />, false, "modules", currentParent),
    "/callcenter-central": (currentParent) => showCallcenter && showTelefonia && hasModuleAccess("callcenter_central.access") && renderMenuItem("/callcenter-central", "Central Callcenter", <PhoneCall className="hero-icon" />, false, "modules", currentParent),
    "/erpfone": (currentParent) => showErpPhone && showTelefonia && hasModuleAccess("erp_phone.access") && renderMenuItem("/erpfone", "ERP-PHONE", <Wrench className="hero-icon" />, false, "modules", currentParent),
    "/voip": (currentParent) => showVoip && showTelefonia && hasModuleAccess("voip.access") && renderMenuItem("/voip", "Telefone", <PhoneCall className="hero-icon" />, false, "modules", currentParent),
    "/asaas-financial": (currentParent) => showAsaas && hasModuleAccess("asaas.access") && renderMenuItem("/asaas-financial", "Painel Asaas", <DollarSign className="hero-icon" />, false, "modules", currentParent),
    "/bancario-cartoes": (currentParent) => showBancarioCartoes && hasModuleAccess("banking_cards.access") && renderMenuItem("/bancario-cartoes", "Bancário/Cartões", <Building2 className="hero-icon" />, false, "modules", currentParent),

    "/campaigns": (currentParent) => showCampaigns && hasModulePermission("allowCampaignsList") && renderMenuItem("/campaigns", i18n.t("campaigns.subMenus.list"), <List className="hero-icon" />, false, "campaigns", currentParent),
    "/voice-campaigns": (currentParent) => showVoiceCampaigns && showCampaigns && hasModulePermission("allowCampaignsList") && renderMenuItem("/voice-campaigns", "Campanha por Voz", <PhoneCall className="hero-icon" />, false, "campaigns", currentParent),
    "/group-campaigns": (currentParent) => showGroupCampaigns && showCampaigns && hasModulePermission("allowCampaignsList") && renderMenuItem("/group-campaigns", "Campanha de Grupo", <MessagesSquare className="hero-icon" />, false, "campaigns", currentParent),
    "/email-campaigns": (currentParent) => showEmailCampaign && hasModulePermission("allowEmailCampaigns") && renderMenuItem("/email-campaigns", "Campanha de E-mail", <Mail className="hero-icon" />, false, "campaigns", currentParent),
    "/contact-lists": (currentParent) => showCampaigns && hasModulePermission("allowContactLists") && renderMenuItem("/contact-lists", i18n.t("campaigns.subMenus.listContacts"), <UsersRound className="hero-icon" />, false, "campaigns", currentParent),

    "/users": (currentParent) => hasPermission(user.profile, "dashboard:view") && hasModulePermission("allowUsers") && renderMenuItem("/users", i18n.t("mainDrawer.listItems.users"), <Users className="hero-icon" />, false, "administration", currentParent),
    "/queues": (currentParent) => hasPermission(user.profile, "dashboard:view") && hasModulePermission("allowQueues") && renderMenuItem("/queues", i18n.t("mainDrawer.listItems.queues"), <ListOrdered className="hero-icon" />, false, "administration", currentParent),
    "/equipes": (currentParent) => hasPermission(user.profile, "dashboard:view") && hasModulePermission("allowTeams") && showEquipe && renderMenuItem("/equipes", "Equipes", <UsersRound className="hero-icon" />, false, "administration", currentParent),
    "/group-management": (currentParent) => hasPermission(user.profile, "dashboard:view") && hasModulePermission("allowSettings") && showGroupManagement && renderMenuItem("/group-management", "Gerenciar Grupos", <MessagesSquare className="hero-icon" />, false, "administration", currentParent),
    "/prompts": (currentParent) => showOpenAi && hasModulePermission("allowAutomations") && hasModulePermission("allowTalkIa") && hasPermission(user.profile, "dashboard:view") && renderMenuItem("/prompts", i18n.t("mainDrawer.listItems.prompts"), <Sparkles className="hero-icon" />, false, "administration", currentParent),
    "/ai-agents": (currentParent) => showOpenAi && hasModulePermission("allowAutomations") && hasModulePermission("allowAiAgents") && hasPermission(user.profile, "dashboard:view") && renderMenuItem("/ai-agents", "Agentes IA", <Sparkles className="hero-icon" />, false, "administration", currentParent),
    "/ai-help-requests": (currentParent) => showOpenAi && hasModulePermission("allowAutomations") && hasModulePermission("allowAiAgents") && hasPermission(user.profile, "dashboard:view") && renderMenuItem("/ai-help-requests", "Solicitações da IA", <LifeBuoy className="hero-icon" />, false, "administration", currentParent),
    "/ai-secretaria": (currentParent) => showOpenAi && hasModulePermission("allowAutomations") && hasModulePermission("allowAiAgents") && hasPermission(user.profile, "dashboard:view") && renderMenuItem("/ai-secretaria", "Ai Secretaria", <Sparkles className="hero-icon" />, false, "administration", currentParent),
    "/mcp-agents": (currentParent) => showOpenAi && hasModulePermission("allowAutomations") && hasModulePermission("allowAiAgents") && hasPermission(user.profile, "dashboard:view") && renderMenuItem("/mcp-agents", "MCP Agents", <Bot className="hero-icon" />, false, "administration", currentParent),
    "/queue-integration": (currentParent) => showIntegrations && hasModulePermission("allowAutomations") && hasModulePermission("allowIntegrations") && hasPermission(user.profile, "dashboard:view") && renderMenuItem("/queue-integration", i18n.t("mainDrawer.listItems.queueIntegration"), <Cable className="hero-icon" />, false, "administration", currentParent),
    "/messages-api": (currentParent) => showExternalApi && hasModulePermission("allowApi") && hasPermission(user.profile, "dashboard:view") && renderMenuItem("/messages-api", i18n.t("mainDrawer.listItems.messagesAPI"), <Code className="hero-icon" />, false, "administration", currentParent),
    "/knowledge-base": (currentParent) => showExternalApi && hasModulePermission("allowKnowledgeBase") && hasPermission(user.profile, "dashboard:view") && renderMenuItem("/knowledge-base", "Base de Conhecimento", <Info className="hero-icon" />, false, "administration", currentParent),
    "/evolution-interactives": (currentParent) => hasPermission(user.profile, "dashboard:view") && hasModulePermission("allowSettings") && showButtonList && renderMenuItem("/evolution-interactives", "Gerenciar Botões/Lista", <List className="hero-icon" />, false, "administration", currentParent),
    "/financeiro": (currentParent) => hasPermission(user.profile, "dashboard:view") && hasModulePermission("allowFinanceiro") && showFinanceiro && renderMenuItem("/financeiro", i18n.t("mainDrawer.listItems.financeiro"), <DollarSign className="hero-icon" />, false, "administration", currentParent),
    "/wallet-management": (currentParent) => showWalletManagement && user.profile === "admin" && hasModulePermission("allowWalletManagement") && renderMenuItem("/wallet-management", "Gerenciamento de Carteira", <DollarSign className="hero-icon" />, false, "administration", currentParent),
    "/settings": (currentParent) => hasPermission(user.profile, "dashboard:view") && hasModulePermission("allowSettings") && renderMenuItem("/settings", i18n.t("mainDrawer.listItems.settings"), <Settings className="hero-icon" />, false, "administration", currentParent),
    "/announcements": (currentParent) => user.super && hasModulePermission("allowAnnouncements") && renderMenuItem("/announcements", i18n.t("mainDrawer.listItems.annoucements"), <Info className="hero-icon" />, false, "administration", currentParent),
    "/companies": (currentParent) => user.super && renderMenuItem("/companies", i18n.t("mainDrawer.listItems.companies"), <Building2 className="hero-icon" />, false, "administration", currentParent),
    "/connections": (currentParent) => hasPermission(user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile, "drawer-admin-items:view") && hasModulePermission("allowConnections") && renderMenuItem("/connections", i18n.t("mainDrawer.listItems.connections"), <ArrowLeftRight className="hero-icon" />, connectionWarning, "administration", currentParent),
    "/allConnections": (currentParent) => user.super && renderMenuItem("/allConnections", i18n.t("mainDrawer.listItems.allConnections"), <Server className="hero-icon" />, false, "administration", currentParent),
  };

  const renderCustomParentItems = (targetMenuKey) => {
    if (!headerConfig?.submenus) return null;
    const itemsMovedToTarget = headerConfig.submenus.filter((submenu) => {
      if (submenu.visible === false) return false;
      if (submenu.parent !== targetMenuKey) return false;
      const defaultParent = getDefaultSubmenuParent(submenu.path);
      return defaultParent !== targetMenuKey;
    });

    if (itemsMovedToTarget.length === 0) return null;

    const renderedNodes = itemsMovedToTarget.map((submenu) => {
      const renderer = itemRenderers[submenu.path];
      return renderer ? renderer(targetMenuKey) : null;
    }).filter(Boolean);

    if (renderedNodes.length === 0) return null;

    return renderMenuSection("Outros Módulos", renderedNodes);
  };

  return (
    <div
      className={`main-list-items ${isMobile ? "mobile" : ""} ${
        isDarkMode ? "dark" : ""
      }`}
    >
      {/* ==================== 1. GESTÃO ==================== */}
      {canShowManagementMenu && (
      <CanWrapper
        role={
          (user.profile === "user" && user.showDashboard === "enabled") ||
          user.allowRealTime === "enabled"
            ? "admin"
            : user.profile
        }
        perform={"drawer-admin-items:view"}
        style={getHeaderMenuStyle("management")}
      >
        <div
          className={`dropdown-container ${managementMenuOpen ? "open" : ""}`}
          style={getHeaderMenuStyle("management")}
          ref={managementContainerRef}
          onMouseEnter={() => openDesktopMenu("management")}
          onMouseLeave={scheduleDesktopClose}
        >
          <button
            ref={managementButtonRef}
            className={`nav-button ${isManagementActive ? "active" : ""}`}
            onClick={toggleManagementMenu}
          >
            {renderHeaderMenuIcon("management", Home)}
            <span>{getHeaderMenu("management")?.label}</span>
            {isMobile && (
              <ChevronDown
                className={`dropdown-icon ${managementMenuOpen ? "open" : ""}`}
              />
            )}
          </button>

          <div
            ref={managementMenuRef}
            className={`dropdown-menu ${
              isMobile && managementMenuOpen ? "open" : ""
            }`}
          >
            <Can
              role={
                user.profile === "user" && user.showDashboard === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() => (
                <>
                  {showDashboardModule &&
                    renderMenuItem(
                      "/",
                      "Dashboard",
                      <BarChart3 className="hero-icon" />,
                      false,
                      "management"
                    )}
                  {showRelatorio &&
                    renderMenuItem(
                      "/reports",
                      i18n.t("mainDrawer.listItems.reports"),
                      <FileText className="hero-icon" />,
                      false,
                      "management"
                    )}
                  {showDashboardModule &&
                    showAnalyticsTemplateWhats &&
                    renderMenuItem(
                      "/analytics-template-whats",
                      "Analytics Template Whats",
                      <BarChart3 className="hero-icon" />,
                      false,
                      "management"
                    )}
                </>
              )}
            />

            {showDashboardModule &&
              showMetaAds &&
              renderMenuSection("Marketing Meta (beta)", [
                renderMenuItem(
                  "/meta-ads/campaigns",
                  "Campanhas",
                  <Megaphone className="hero-icon" />,
                  false,
                  "management"
                ),
                renderMenuItem(
                  "/meta-ads/analytics",
                  "Analytics",
                  <BarChart3 className="hero-icon" />,
                  false,
                  "management"
                ),
                renderMenuItem(
                  "/meta-ads/reports",
                  "Relatórios",
                  <FileText className="hero-icon" />,
                  false,
                  "management"
                ),
                renderMenuItem(
                  "/meta-ads/roi",
                  "ROI",
                  <DollarSign className="hero-icon" />,
                  false,
                  "management"
                ),
              ])}

            {showDashboardModule && (
              <Can
                role={
                  user.profile === "user" && user.allowRealTime === "enabled"
                    ? "admin"
                    : user.profile
                }
                perform={"drawer-admin-items:view"}
                yes={() =>
                  renderMenuItem(
                    "/moments",
                    i18n.t("mainDrawer.listItems.chatsTempoReal"),
                    <Table className="hero-icon" />,
                    false,
                    "management"
                  )
                }
              />
            )}
            {renderCustomParentItems("management")}
          </div>
        </div>
      </CanWrapper>
      )}

      {/* ==================== 2. ATENDIMENTO ==================== */}
      {hasModulePermission("allowAttendance") && (
      <div
        className={`dropdown-container ${ticketsMenuOpen ? "open" : ""}`}
        style={getHeaderMenuStyle("attendance")}
        ref={ticketsContainerRef}
        onMouseEnter={() => openDesktopMenu("tickets")}
        onMouseLeave={scheduleDesktopClose}
      >
        <button
          ref={ticketsButtonRef}
          className={`nav-button ${isTicketsActive ? "active" : ""}`}
          onClick={toggleTicketsMenu}
        >
          {renderHeaderMenuIcon("attendance", MessageSquare)}
          <span>{getHeaderMenu("attendance")?.label}</span>
          {isMobile && (
            <ChevronDown
              className={`dropdown-icon ${ticketsMenuOpen ? "open" : ""}`}
            />
          )}
        </button>

        <div
          ref={ticketsMenuRef}
          className={`dropdown-menu ${
            isMobile && ticketsMenuOpen ? "open" : ""
          }`}
        >
          {renderMenuSection("Atendimento", [
            showTickets &&
              renderMenuItem(
                "/tickets",
                i18n.t("mainDrawer.listItems.tickets"),
                <MessagesSquare className="hero-icon" />,
                false,
                "attendance"
              ),
            showEmailModule &&
              hasModulePermission("allowEmail") &&
              renderMenuItem(
                "/emails",
                "E-mail",
                <Paperclip className="hero-icon" />,
                emailWarning,
                "attendance"
              ),
            renderMenuItem(
              "/quick-messages",
              i18n.t("mainDrawer.listItems.quickMessages"),
              <Zap className="hero-icon" />,
              false,
              "attendance"
            ),
            showSuporte &&
              renderMenuItem(
                "/support-tickets",
                "Suporte",
                <LifeBuoy className="hero-icon" />,
                false,
                "attendance"
              ),
            showInternalPortal &&
              hasModulePermission("allowInternalPortal") &&
              renderMenuItem(
                "/portal-interno",
                "Portal Interno",
                <Info className="hero-icon" />,
                false,
                "attendance"
              ),
            showKanban &&
              user?.profile !== "motoboy" &&
              hasModulePermission("allowKanban") &&
              renderMenuItem(
                "/kanban",
                i18n.t("mainDrawer.listItems.kanban"),
                <Columns className="hero-icon" />,
                false,
                "attendance"
              ),
            showInternalChat &&
              hasModulePermission("allowInternalChat") &&
              renderMenuItem(
                "/chats",
                i18n.t("mainDrawer.listItems.chats"),
                <div
                  className={`notification-icon ${
                    invisible ? "" : "has-notification"
                  }`}
                >
                  <MessageSquare className="hero-icon" />
                </div>,
                false,
                "attendance"
              ),
          ])}

          {renderMenuSection("Contatos e Organização", [
            hasModulePermission("allowContacts") &&
              renderMenuItem(
                "/contacts",
                i18n.t("mainDrawer.listItems.contacts"),
                <User className="hero-icon" />,
                false,
                "attendance"
              ),
            showWallet &&
              hasModulePermission("allowMyWallet") &&
              renderMenuItem(
                "/my-wallet",
                "Minha Carteira",
                <DollarSign className="hero-icon" />,
                false,
                "attendance"
              ),
            hasModulePermission("allowTags") &&
              renderMenuItem(
                "/tags",
                i18n.t("mainDrawer.listItems.tags"),
                <Tag className="hero-icon" />,
                false,
                "attendance"
              ),
          ])}

          {renderMenuSection("Produtividade", [
            showTasks &&
              hasModulePermission("allowTasks") &&
              renderMenuItem(
                "/todolist",
                i18n.t("todo.task"),
                <List className="hero-icon" />,
                false,
                "attendance"
              ),
            showSchedules &&
              hasModulePermission("allowSchedules") &&
              renderMenuItem(
                "/schedules",
                i18n.t("mainDrawer.listItems.schedules"),
                <Clock className="hero-icon" />,
                false,
                "attendance"
              ),
            hasHelps &&
              renderMenuItem(
                "/helps",
                i18n.t("mainDrawer.listItems.helps"),
                <HelpCircle className="hero-icon" />,
                false,
                "attendance"
              ),
          ])}

          {renderCustomParentItems("attendance")}
        </div>
      </div>
      )}

      {/* ==================== 3. MÓDULOS ESPECÍFICOS (NOVO) ==================== */}
      {hasAnySpecificModule && (
      <div
        className={`dropdown-container ${modulesMenuOpen ? "open" : ""}`}
        style={getHeaderMenuStyle("modules")}
        ref={modulesContainerRef}
        onMouseEnter={() => openDesktopMenu("modules")}
        onMouseLeave={scheduleDesktopClose}
      >
        <button
          ref={modulesButtonRef}
          className={`nav-button ${isModulesActive ? "active" : ""}`}
          onClick={toggleModulesMenu}
        >
          {renderHeaderMenuIcon("modules", FolderOpen)}
          <span>{getHeaderMenu("modules")?.label}</span>
          {isMobile && (
            <ChevronDown
              className={`dropdown-icon ${modulesMenuOpen ? "open" : ""}`}
            />
          )}
        </button>

        <div
          ref={modulesMenuRef}
          className={`dropdown-menu ${
            isMobile && modulesMenuOpen ? "open" : ""
          }`}
        >
          {showDocSignature &&
            renderMenuSection("Documentação", [
              renderMenuItem(
                "/document-signature",
                "Assinar com Certificado",
                <FileText className="hero-icon" />,
                false,
                "modules"
              ),
            ])}

          {canShowJuridicoSection &&
            renderMenuSection("Jurídico", [
              showConsultaProcesso &&
                renderMenuItem(
                  "/consulta-processo",
                  "Consulta Processo",
                  <Briefcase className="hero-icon" />,
                  false,
                  "modules"
                ),
              showAdvoc360 &&
                renderMenuItem(
                  "/advoc360",
                  "Advoc-360",
                  <Gavel className="hero-icon" />,
                  false,
                  "modules"
                ),
            ])}

          {canShowRedeSocialSection &&
            renderMenuSection("Rede Social", [
              showGatilho &&
                renderMenuItem(
                  "/social-triggers",
                  "Gatilhos",
                  <Share2 className="hero-icon" />,
                  false,
                  "modules"
                ),
              showFlowBuilder &&
                renderMenuSubmenu(
                  "FlowApp",
                  <Workflow className="hero-icon" />,
                  [
                    renderMenuItem(
                      "/flow-process",
                      "Processos",
                      <Workflow className="hero-icon" />,
                      false,
                      "modules"
                    ),
                    renderMenuItem(
                      "/flow-process/templates",
                      "Templates",
                      <Layers className="hero-icon" />,
                      false,
                      "modules"
                    ),
                    renderMenuItem(
                      "/flow-process/categories",
                      "Categorias",
                      <Folder className="hero-icon" />,
                      false,
                      "modules"
                    ),
                    renderMenuItem(
                      "/flow-process/reports",
                      "Relatórios",
                      <BarChart3 className="hero-icon" />,
                      false,
                      "modules"
                    ),
                  ],
                  "flowapp-submenu",
                  location.pathname.startsWith("/flow-process")
                ),
              showRedeSocial &&
                renderMenuItem(
                  "/social-posts",
                  "Rede Social",
                  <Share2 className="hero-icon" />,
                  false,
                  "modules"
                ),
              showWhatsappTemplates &&
                renderMenuItem(
                  "/whatsapp-templates",
                  "Whatsapp Templates",
                  <FileText className="hero-icon" />,
                  false,
                  "modules"
                ),
              canAccessMeetingRooms &&
                showMeetingRooms &&
                renderMenuItem(
                  "/meeting-rooms",
                  "Sala de reunião",
                  <Video className="hero-icon" />,
                  false,
                  "modules"
                ),
            ])}

          {canShowSaudeBelezaSection &&
            renderMenuSection("Saúde e Beleza", [
              showSchedules &&
                hasModulePermission("allowSchedules") &&
                showAgendacare &&
                hasModulePermission("allowAgendaCare") &&
                renderMenuItem(
                  "/agendacare",
                  "AgendaCare",
                  <Stethoscope className="hero-icon" />,
                  false,
                  "modules"
                ),
              showSchedules &&
                hasModulePermission("allowSchedules") &&
                showOdontocare &&
                hasModulePermission("allowOdontoCare") &&
                renderMenuItem(
                  "/odontocare",
                  "OdontoCare",
                  <Smile className="hero-icon" />,
                  false,
                  "modules"
                ),
              showBarber &&
                hasModulePermission("allowBarber") &&
                renderMenuItem(
                  "/barber",
                  "Barbearia",
                  <Scissors className="hero-icon" />,
                  false,
                  "modules"
                ),
              showEducare &&
                renderMenuItem(
                  "/educare",
                  "EduCare",
                  <GraduationCap className="hero-icon" />,
                  false,
                  "modules"
                ),
              showRotaVendas360 &&
                renderMenuItem(
                  "/rotsa_360",
                  "RotaVendas 360(BETA)",
                  <Map className="hero-icon" />,
                  false,
                  "modules"
                ),
              showPetcare &&
                hasModulePermission("allowPetCare") &&
                renderMenuItem(
                  "/petcare",
                  "PetCare 360",
                  <PawPrint className="hero-icon" />,
                  false,
                  "modules"
                ),
              showServico &&
                renderMenuItem(
                  "/services",
                  "Serviços",
                  <Scissors className="hero-icon" />,
                  false,
                  "modules"
                ),
            ])}

          {canShowVendasDeliverySection &&
            renderMenuSection("Vendas e Delivery", [
              showPdv &&
                hasModulePermission("allowCashierPdv") &&
                renderMenuItem(
                  "/pdv",
                  "Caixa PDV",
                  <ShoppingCart className="hero-icon" />,
                  false,
                  "modules"
                ),
              showPdv &&
                hasModulePermission("allowCashierPdv") &&
                renderMenuItem(
                  "/loja-online",
                  "Loja Online",
                  <ShoppingCart className="hero-icon" />,
                  false,
                  "modules"
                ),
              showComidaNaPorta &&
                hasModulePermission("allowComidaNaPorta") &&
                renderMenuItem(
                  "/comida-na-porta",
                  "Comida na Porta",
                  <ShoppingCart className="hero-icon" />,
                  false,
                  "modules"
                ),
              showComidaNaPorta &&
                hasModulePermission("allowComidaNaPorta") &&
                renderMenuItem(
                  "/motoqueiro",
                  "Motoqueiro",
                  <Truck className="hero-icon" />,
                  false,
                  "modules"
                ),
              showWooCommerce &&
                renderMenuItem(
                  "/woocommerce",
                  "WooCommerce",
                  <ShoppingCart className="hero-icon" />,
                  false,
                  "modules"
                ),
            ])}

          {canShowTelefoniaSection &&
            renderMenuSection("Telefonia", [
              showCallcenter &&
                showTelefonia &&
                hasModulePermission("allowCallcenterUser") &&
                renderMenuItem(
                  "/callcenter",
                  "Callcenter",
                  <PhoneCall className="hero-icon" />,
                  false,
                  "modules"
                ),
              showCallcenter &&
                showTelefonia &&
                hasPermission(user.profile, "dashboard:view") &&
                hasModulePermission("allowCallcenterAdmin") &&
                renderMenuItem(
                  "/callcenter-central",
                  "Central Callcenter",
                  <PhoneCall className="hero-icon" />,
                  false,
                  "modules"
                ),
              showErpPhone &&
                showTelefonia &&
                hasModulePermission("allowErpPhone") &&
                renderMenuItem(
                  "/erpfone",
                  "ERP-PHONE",
                  <Wrench className="hero-icon" />,
                  false,
                  "modules"
                ),
              showVoip &&
                showTelefonia &&
                renderMenuItem(
                  "/voip",
                  "Telefone",
                  <PhoneCall className="hero-icon" />,
                  false,
                  "modules"
                ),
            ])}

          {canShowFinanceiroModuloSection &&
            renderMenuSection("Financeiro", [
              showAsaas &&
                hasModulePermission("allowAsaas") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/asaas-financial",
                  "Painel Asaas",
                  <DollarSign className="hero-icon" />,
                  false,
                  "modules"
                ),
              showBancarioCartoes &&
                renderMenuItem(
                  "/bancario-cartoes",
                  "Bancário/Cartões",
                  <Building2 className="hero-icon" />,
                  false,
                  "modules"
                ),
            ])}
          {renderCustomParentItems("modules")}
        </div>
      </div>
      )}

      {/* ==================== 4. CAMPANHAS ==================== */}
      {((showCampaigns && hasModulePermission("allowCampaignsList")) ||
        (showEmailCampaign && hasModulePermission("allowEmailCampaigns")) ||
        (showCampaigns && hasModulePermission("allowContactLists"))) &&
        hasModulePermission("allowCampaigns") && (
        <CanWrapper role={user.profile} perform="dashboard:view" style={getHeaderMenuStyle("campaigns")}>
          <div
            className={`dropdown-container ${campaignsMenuOpen ? "open" : ""}`}
            style={getHeaderMenuStyle("campaigns")}
            ref={campaignsContainerRef}
            onMouseEnter={() => openDesktopMenu("campaigns")}
            onMouseLeave={scheduleDesktopClose}
          >
            <button
              ref={campaignsButtonRef}
              className={`nav-button ${isCampaignRouteActive ? "active" : ""}`}
              onClick={toggleCampaignsMenu}
            >
              {renderHeaderMenuIcon("campaigns", Megaphone)}
              <span>{getHeaderMenu("campaigns")?.label}</span>
              {isMobile && (
                <ChevronDown
                  className={`dropdown-icon ${campaignsMenuOpen ? "open" : ""}`}
                />
              )}
            </button>

            <div
              ref={campaignsMenuRef}
              className={`dropdown-menu ${
                isMobile && campaignsMenuOpen ? "open" : ""
              }`}
            >
              {showCampaigns &&
                hasModulePermission("allowCampaignsList") &&
                renderMenuItem(
                  "/campaigns",
                  i18n.t("campaigns.subMenus.list"),
                  <List className="hero-icon" />,
                  false,
                  "campaigns"
                )}
              {showVoiceCampaigns &&
                showCampaigns &&
                hasModulePermission("allowCampaignsList") &&
                renderMenuItem(
                  "/voice-campaigns",
                  "Campanha por Voz",
                  <PhoneCall className="hero-icon" />,
                  false,
                  "campaigns"
                )}
              {showGroupCampaigns &&
                showCampaigns &&
                hasModulePermission("allowCampaignsList") &&
                renderMenuItem(
                  "/group-campaigns",
                  "Campanha de Grupo",
                  <MessagesSquare className="hero-icon" />,
                  false,
                  "campaigns"
                )}
              {showEmailCampaign &&
                hasModulePermission("allowEmailCampaigns") &&
                renderMenuItem(
                  "/email-campaigns",
                  "Campanha de E-mail",
                  <Mail className="hero-icon" />,
                  false,
                  "campaigns"
                )}
              {showCampaigns &&
                hasModulePermission("allowContactLists") &&
                renderMenuItem(
                  "/contact-lists",
                  i18n.t("campaigns.subMenus.listContacts"),
                  <UsersRound className="hero-icon" />,
                  false,
                  "campaigns"
                )}
              {renderCustomParentItems("campaigns")}
            </div>
          </div>
        </CanWrapper>
      )}

      {/* ==================== 5. ADMINISTRAÇÃO ==================== */}
      <CanWrapper
        role={
          user.profile === "user" && user.allowConnections === "enabled"
            ? "admin"
            : user.profile
        }
        perform="dashboard:view"
        style={getHeaderMenuStyle("administration")}
      >
        <div
          className={`dropdown-container ${adminMenuOpen ? "open" : ""}`}
          style={getHeaderMenuStyle("administration")}
          ref={adminContainerRef}
          onMouseEnter={() => openDesktopMenu("admin")}
          onMouseLeave={scheduleDesktopClose}
        >
          <button
            ref={adminButtonRef}
            className={`nav-button ${isAdminActive ? "active" : ""}`}
            onClick={toggleAdminMenu}
          >
            {renderHeaderMenuIcon("administration", Cog)}
            <span>{getHeaderMenu("administration")?.label}</span>
            {isMobile && (
              <ChevronDown
                className={`dropdown-icon ${adminMenuOpen ? "open" : ""}`}
              />
            )}
          </button>
          <div
            ref={adminMenuRef}
            className={`dropdown-menu ${
              isMobile && adminMenuOpen ? "open" : ""
            }`}
          >
            {renderMenuSection("Pessoas e Equipes", [
              hasPermission(user.profile, "dashboard:view") &&
                hasModulePermission("allowUsers") &&
                renderMenuItem(
                  "/users",
                  i18n.t("mainDrawer.listItems.users"),
                  <Users className="hero-icon" />,
                  false,
                  "administration"
                ),
              hasPermission(user.profile, "dashboard:view") &&
                hasModulePermission("allowQueues") &&
                renderMenuItem(
                  "/queues",
                  i18n.t("mainDrawer.listItems.queues"),
                  <ListOrdered className="hero-icon" />,
                  false,
                  "administration"
                ),
              hasPermission(user.profile, "dashboard:view") &&
                hasModulePermission("allowTeams") &&
                showEquipe &&
                renderMenuItem(
                  "/equipes",
                  "Equipes",
                  <UsersRound className="hero-icon" />,
                  false,
                  "administration"
                ),
              hasPermission(user.profile, "dashboard:view") &&
                hasModulePermission("allowSettings") &&
                showGroupManagement &&
                renderMenuItem(
                  "/group-management",
                  "Gerenciar Grupos",
                  <MessagesSquare className="hero-icon" />,
                  false,
                  "administration"
                ),
            ])}

            {renderMenuSection("Inteligência Artificial", [
              showOpenAi &&
                hasModulePermission("allowAutomations") &&
                hasModulePermission("allowTalkIa") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/prompts",
                  i18n.t("mainDrawer.listItems.prompts"),
                  <Sparkles className="hero-icon" />,
                  false,
                  "administration"
                ),
              showOpenAi &&
                hasModulePermission("allowAutomations") &&
                hasModulePermission("allowAiAgents") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/ai-agents",
                  "Agentes IA",
                  <Sparkles className="hero-icon" />,
                  false,
                  "administration"
                ),
              showOpenAi &&
                hasModulePermission("allowAutomations") &&
                hasModulePermission("allowAiAgents") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/ai-help-requests",
                  "Solicitações da IA",
                  <LifeBuoy className="hero-icon" />,
                  false,
                  "administration"
                ),
              showOpenAi &&
                hasModulePermission("allowAutomations") &&
                hasModulePermission("allowAiAgents") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/ai-secretaria",
                  "Ai Secretaria",
                  <Sparkles className="hero-icon" />,
                  false,
                  "administration"
                ),
              showOpenAi &&
                hasModulePermission("allowAutomations") &&
                hasModulePermission("allowAiAgents") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/mcp-agents",
                  "MCP Agents",
                  <Bot className="hero-icon" />,
                  false,
                  "administration"
                ),
            ])}

            {renderMenuSection("Integrações e API", [
              showIntegrations &&
                hasModulePermission("allowAutomations") &&
                hasModulePermission("allowIntegrations") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/queue-integration",
                  i18n.t("mainDrawer.listItems.queueIntegration"),
                  <Cable className="hero-icon" />,
                  false,
                  "administration"
                ),
              showExternalApi &&
                hasModulePermission("allowApi") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/messages-api",
                  i18n.t("mainDrawer.listItems.messagesAPI"),
                  <Code className="hero-icon" />,
                  false,
                  "administration"
                ),
              showExternalApi &&
                hasModulePermission("allowKnowledgeBase") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/knowledge-base",
                  "Base de Conhecimento",
                  <Info className="hero-icon" />,
                  false,
                  "administration"
                ),
              hasPermission(user.profile, "dashboard:view") &&
                hasModulePermission("allowSettings") &&
                showButtonList &&
                renderMenuItem(
                  "/evolution-interactives",
                  "Gerenciar Botões/Lista",
                  <List className="hero-icon" />,
                  false,
                  "administration"
                ),
              showAsaas &&
                hasModulePermission("allowAsaas") &&
                hasPermission(user.profile, "dashboard:view") &&
                renderMenuItem(
                  "/asaas-financial",
                  "Asaas",
                  <DollarSign className="hero-icon" />,
                  false,
                  "administration"
                ),
            ])}

            {renderMenuSection("Financeiro", [
              hasPermission(user.profile, "dashboard:view") &&
                hasModulePermission("allowFinanceiro") &&
                showFinanceiro &&
                renderMenuItem(
                  "/financeiro",
                  i18n.t("mainDrawer.listItems.financeiro"),
                  <DollarSign className="hero-icon" />,
                  false,
                  "administration"
                ),
              renderMenuItem(
                "/bancario-cartoes",
                "Bancário/Cartões",
                <Building2 className="hero-icon" />,
                false,
                "administration"
              ),
              showWalletManagement &&
                user.profile === "admin" &&
                hasModulePermission("allowWalletManagement") &&
                renderMenuItem(
                  "/wallet-management",
                  "Gerenciamento de Carteira",
                  <DollarSign className="hero-icon" />,
                  false,
                  "administration"
                ),
            ])}

            {renderMenuSection("Configurações", [
              hasPermission(user.profile, "dashboard:view") &&
                hasModulePermission("allowSettings") &&
                renderMenuItem(
                  "/settings",
                  i18n.t("mainDrawer.listItems.settings"),
                  <Settings className="hero-icon" />,
                  false,
                  "administration"
                ),
              user.super &&
                hasModulePermission("allowAnnouncements") &&
                renderMenuItem(
                  "/announcements",
                  i18n.t("mainDrawer.listItems.annoucements"),
                  <Info className="hero-icon" />,
                  false,
                  "administration"
                ),
              user.super &&
                renderMenuItem(
                  "/companies",
                  i18n.t("mainDrawer.listItems.companies"),
                  <Building2 className="hero-icon" />,
                  false,
                  "administration"
                ),
            ])}

            {renderMenuSection("Conexões", [
              hasPermission(
                user.profile === "user" && user.allowConnections === "enabled"
                  ? "admin"
                  : user.profile,
                "drawer-admin-items:view"
              ) &&
                hasModulePermission("allowConnections") &&
                renderMenuItem(
                  "/connections",
                  i18n.t("mainDrawer.listItems.connections"),
                  <ArrowLeftRight className="hero-icon" />,
                  connectionWarning,
                  "administration"
                ),
              user.super &&
                renderMenuItem(
                  "/allConnections",
                  i18n.t("mainDrawer.listItems.allConnections"),
                  <Server className="hero-icon" />,
                  false,
                  "administration"
                ),
            ])}
            {renderCustomParentItems("administration")}
          </div>
        </div>
      </CanWrapper>
    </div>
  );
};

export default MainListItems;
